"use client";

import { useState, useMemo } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

interface Bike {
  id: number;
  name: string;
  category: string;
  price: string;
  originalPrice: string | null;
  image: string;
  alt: string;
  tags: string[];
  badge: string;
  badgeColor: string;
  rating: number;
  reviews: number;
}

const allBikes: Bike[] = [
{
  id: 1,
  name: "Apex Road Racer",
  category: "Road",
  price: "$2,499",
  originalPrice: "$2,799",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_145ed6a1a-1765020529932.png",
  alt: "Sleek red road bicycle with carbon frame and drop handlebars",
  tags: ["Carbon Frame", "22-Speed", "Disc Brakes"],
  badge: "Bestseller",
  badgeColor: "bg-amber text-charcoal",
  rating: 4.9,
  reviews: 128
},
{
  id: 2,
  name: "Summit MTB Pro",
  category: "Mountain",
  price: "$3,199",
  originalPrice: null,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_170e7c032-1770577686848.png",
  alt: "Black full-suspension mountain bike on rocky dirt trail",
  tags: ["Full Suspension", '29" Wheels', "Hydraulic Brakes"],
  badge: "New 2026",
  badgeColor: "bg-charcoal text-cream",
  rating: 4.8,
  reviews: 94
},
{
  id: 3,
  name: "Urban Glide 7",
  category: "City",
  price: "$899",
  originalPrice: "$1,099",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_183aebbd5-1767045447128.png",
  alt: "Silver city commuter bike with fenders and rear rack",
  tags: ["Lightweight", "7-Speed", "Fenders Included"],
  badge: "Best Value",
  badgeColor: "bg-green-600 text-white",
  rating: 4.7,
  reviews: 211
},
{
  id: 4,
  name: "E-Volt 500W",
  category: "Electric",
  price: "$4,199",
  originalPrice: null,
  image: "https://images.unsplash.com/photo-1669183013871-08908e0e222a",
  alt: "Yellow electric bicycle with integrated battery and LCD display",
  tags: ["500W Motor", "80km Range", "USB Charging"],
  badge: "Electric",
  badgeColor: "bg-blue-600 text-white",
  rating: 4.9,
  reviews: 76
},
{
  id: 5,
  name: "Trail Blazer 29",
  category: "Mountain",
  price: "$1,899",
  originalPrice: "$2,199",
  image: "https://images.unsplash.com/photo-1727534229529-e96ef0fa6c79",
  alt: "Orange hardtail mountain bike with wide knobby tires on forest trail",
  tags: ["Hardtail", "29\" Wheels", "Air Fork"],
  badge: "Sale",
  badgeColor: "bg-red-500 text-white",
  rating: 4.6,
  reviews: 58
},
{
  id: 6,
  name: "Velodrome Sprint",
  category: "Road",
  price: "$1,599",
  originalPrice: null,
  image: "https://images.unsplash.com/photo-1562854288-2845dfef569e",
  alt: "White and blue road racing bicycle with aerodynamic frame",
  tags: ["Aero Frame", "11-Speed", "Rim Brakes"],
  badge: "Popular",
  badgeColor: "bg-purple-600 text-white",
  rating: 4.5,
  reviews: 143
},
{
  id: 7,
  name: "City Cruiser Classic",
  category: "City",
  price: "$649",
  originalPrice: "$799",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_173bcc8c7-1766742563537.png",
  alt: "Vintage-style cream city bicycle with basket and brown leather saddle",
  tags: ["Step-Through", "3-Speed", "Basket Included"],
  badge: "Classic",
  badgeColor: "bg-stone-500 text-white",
  rating: 4.4,
  reviews: 189
},
{
  id: 8,
  name: "E-Commuter Pro",
  category: "Electric",
  price: "$2,899",
  originalPrice: "$3,199",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4ca6062-1771121390303.png",
  alt: "Matte black electric commuter bike with integrated lights and cargo rack",
  tags: ["250W Motor", "60km Range", "Integrated Lights"],
  badge: "Eco Choice",
  badgeColor: "bg-emerald-600 text-white",
  rating: 4.7,
  reviews: 62
},
{
  id: 9,
  name: "Kids Rocket 20",
  category: "Kids",
  price: "$349",
  originalPrice: "$449",
  image: "https://images.unsplash.com/photo-1669302164503-a426ec072aa6",
  alt: "Bright red kids bicycle with training wheels and colorful streamers",
  tags: ["20\" Wheels", "Training Wheels", "Safety Brakes"],
  badge: "Kids",
  badgeColor: "bg-pink-500 text-white",
  rating: 4.8,
  reviews: 97
}];


const categories = ["All", "Road", "Mountain", "City", "Electric", "Kids"];

export default function ProductsPage() {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [cartItems, setCartItems] = useState<number[]>([]);

  const filteredBikes = useMemo(() => {
    return allBikes.filter((bike) => {
      const matchesCategory =
      activeCategory === "All" || bike.category === activeCategory;
      const matchesSearch =
      bike.name.toLowerCase().includes(search.toLowerCase()) ||
      bike.category.toLowerCase().includes(search.toLowerCase()) ||
      bike.tags.some((tag) =>
      tag.toLowerCase().includes(search.toLowerCase())
      );
      return matchesCategory && matchesSearch;
    });
  }, [search, activeCategory]);

  const handleAddToCart = (id: number) => {
    setCartItems((prev) =>
    prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  return (
    <>
      <div className="grain-overlay" aria-hidden="true" />
      <Header />
      <main className="min-h-screen bg-cream pt-28 pb-20">
        {/* Page Header */}
        <section className="px-6 mb-12">
          <div className="max-w-7xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-amber mb-3">
              2026 Collection
            </p>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <h1 className="font-heading text-5xl md:text-6xl font-black leading-none tracking-tight text-charcoal">
                All <span className="italic font-light">Bikes.</span>
              </h1>
              <p className="text-stone-warm max-w-sm">
                Browse our full collection of {allBikes.length}+ handpicked bicycles — performance tested and rider-approved.
              </p>
            </div>
          </div>
        </section>

        {/* Search & Filters */}
        <section className="px-6 mb-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
              {/* Search Bar */}
              <div className="relative flex-1 max-w-md">
                <Icon
                  name="MagnifyingGlassIcon"
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-warm pointer-events-none" />
                
                <input
                  type="text"
                  placeholder="Search bikes, categories, features…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-full border border-stone-border bg-white/70 backdrop-blur-sm text-charcoal placeholder:text-stone-warm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber/40 focus:border-amber transition-all duration-200" />
                
                {search &&
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-warm hover:text-charcoal transition-colors"
                  aria-label="Clear search">
                  
                    <Icon name="XMarkIcon" size={16} />
                  </button>
                }
              </div>

              {/* Category Filters */}
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) =>
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 ${
                  activeCategory === cat ?
                  "bg-charcoal text-cream shadow-md" :
                  "bg-white/70 border border-stone-border text-stone-warm hover:border-charcoal hover:text-charcoal"}`
                  }>
                  
                    {cat}
                  </button>
                )}
              </div>
            </div>

            {/* Results count */}
            <p className="mt-4 text-sm text-stone-warm">
              Showing{" "}
              <span className="font-bold text-charcoal">{filteredBikes.length}</span>{" "}
              {filteredBikes.length === 1 ? "bike" : "bikes"}
              {activeCategory !== "All" &&
              <span> in <span className="font-bold text-charcoal">{activeCategory}</span></span>
              }
              {search &&
              <span> for &ldquo;<span className="font-bold text-charcoal">{search}</span>&rdquo;</span>
              }
            </p>
          </div>
        </section>

        {/* Products Grid */}
        <section className="px-6">
          <div className="max-w-7xl mx-auto">
            {filteredBikes.length === 0 ?
            <div className="text-center py-24">
                <div className="w-16 h-16 rounded-full bg-stone-border/40 flex items-center justify-center mx-auto mb-4">
                  <Icon name="MagnifyingGlassIcon" size={28} className="text-stone-warm" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-charcoal mb-2">No bikes found</h3>
                <p className="text-stone-warm mb-6">Try adjusting your search or filter criteria.</p>
                <button
                onClick={() => {setSearch("");setActiveCategory("All");}}
                className="btn-amber px-6 py-3 rounded-full text-sm font-bold">
                
                  Clear Filters
                </button>
              </div> :

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredBikes.map((bike) => {
                const inCart = cartItems.includes(bike.id);
                return (
                  <div key={bike.id} className="bike-card glass-card rounded-4xl overflow-hidden flex flex-col">
                      {/* Image */}
                      <div className="relative overflow-hidden" style={{ height: "220px" }}>
                        <AppImage
                        src={bike.image}
                        alt={bike.alt}
                        fill
                        className="card-image object-cover" />
                      
                        <span
                        className={`amber-badge absolute top-4 left-4 px-3 py-1 rounded-full ${
                        bike.badgeColor}`
                        }>
                        
                          {bike.badge}
                        </span>
                        {inCart &&
                      <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center shadow-md">
                            <Icon name="CheckIcon" size={14} className="text-white" />
                          </div>
                      }
                      </div>

                      {/* Content */}
                      <div className="p-5 flex flex-col flex-1">
                        <p className="text-xs font-bold uppercase tracking-widest text-stone-warm mb-1">
                          {bike.category}
                        </p>
                        <h3 className="font-heading text-xl font-bold text-charcoal mb-2">
                          {bike.name}
                        </h3>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {bike.tags.map((tag) =>
                        <span
                          key={tag}
                          className="text-xs bg-stone-border/50 text-charcoal px-2.5 py-1 rounded-full font-medium">
                          
                              {tag}
                            </span>
                        )}
                        </div>

                        {/* Rating */}
                        <div className="flex items-center gap-1.5 mb-4">
                          <div className="flex items-center gap-0.5">
                            {[...Array(5)].map((_, i) =>
                          <Icon
                            key={i}
                            name="StarIcon"
                            size={12}
                            className={i < Math.floor(bike.rating) ? "text-amber" : "text-stone-border"} />

                          )}
                          </div>
                          <span className="text-xs font-bold text-charcoal">{bike.rating}</span>
                          <span className="text-xs text-stone-warm">({bike.reviews})</span>
                        </div>

                        {/* Price & Actions */}
                        <div className="mt-auto flex items-center justify-between gap-3">
                          <div>
                            <span className="font-heading text-xl font-bold text-charcoal">
                              {bike.price}
                            </span>
                            {bike.originalPrice &&
                          <span className="text-stone-warm line-through text-sm ml-2">
                                {bike.originalPrice}
                              </span>
                          }
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                            className="btn-outline px-4 py-2 rounded-full text-xs flex items-center gap-1.5"
                            aria-label={`View ${bike.name}`}>
                            
                              <Icon name="EyeIcon" size={13} />
                              View
                            </button>
                            <button
                            onClick={() => handleAddToCart(bike.id)}
                            className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all duration-200 ${
                            inCart ?
                            "bg-green-500 text-white" : "btn-amber"}`
                            }
                            aria-label={inCart ? `Remove ${bike.name} from cart` : `Add ${bike.name} to cart`}>
                            
                              <Icon
                              name={inCart ? "CheckIcon" : "ShoppingCartIcon"}
                              size={13}
                              className={inCart ? "text-white" : "text-charcoal"} />
                            
                              {inCart ? "Added" : "Cart"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>);

              })}
              </div>
            }
          </div>
        </section>
      </main>
      <Footer />
    </>);

}